import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "@/components/ui/inputForm";
import { Button } from "@/components/ui/button";
import { mascaraCnpj, mascaraCpf, mascaraCelular } from "@/utils/masks";
import { postUnidade, getUnidades, getUnidadeById } from "@/services/unidade";
import { message } from "antd";

const defaultValues = {
  nome: "",
  fatorAjusteFinanceiro: "",
  ativo: true,
  disponivelSite: true,
  cnpj: "",
  enderecoCompleto: "",
  responsavel: "",
  responsavelCpf: "",
  rg: "",
  orgaoRg: "",
  telefone: "",
  email: "",
};

export default function CadastroUnidade() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues,
    mode: "onBlur",
  });

  useEffect(() => {
    if (isEdit && id) {
      getUnidadeById(id).then((res) => {
        if (res.status === 200) {
          const unidade = res.data;
          if (unidade) {
            let fatorAjuste = unidade.fatorAjusteFinanceiro;
            let fatorAjusteStr = "";
            if (fatorAjuste !== null && fatorAjuste !== undefined && fatorAjuste !== "") {
              fatorAjusteStr = Number(fatorAjuste).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            }
            reset({
              ...defaultValues,
              ...unidade,
              cnpj: unidade.cnpj ? mascaraCnpj(unidade.cnpj.replace(/\D/g, "")) : "",
              responsavelCpf: unidade.responsavelCpf ? mascaraCpf(unidade.responsavelCpf.replace(/\D/g, "")) : "",
              fatorAjusteFinanceiro: fatorAjusteStr,
              telefone: unidade.telefone ? mascaraCelular(unidade.telefone.replace(/\D/g, "")) : "",
              email: unidade.email ?? "",
              ativo: unidade.ativo !== undefined ? unidade.ativo : true,
              disponivelSite:
                unidade.disponivelSite !== undefined ? unidade.disponivelSite : true,
            });
          }
        }
      });
    } else {
      reset(defaultValues);
    }
  }, [id, isEdit, reset]);

  const onSubmit = async (data: any) => {
    try {
      // Remove masks
      const payload = {
        ...data,
        cnpj: data.cnpj ? data.cnpj.replace(/\D/g, "") : "",
        responsavelCpf: data.responsavelCpf ? data.responsavelCpf.replace(/\D/g, "") : "",
        telefone: data.telefone ? data.telefone.replace(/\D/g, "") : "",
        fatorAjusteFinanceiro: data.fatorAjusteFinanceiro ? parseFloat(data.fatorAjusteFinanceiro.replace(",", ".")) : 0,
      };
      let response;
      if (isEdit && id) {
        response = await postUnidade({ ...payload, id });
      } else {
        response = await postUnidade(payload);
      }
      if (response.status === 200 || response.status === 201) {
        message.success("Unidade salva com sucesso!");
        navigate("/unidades");
      }
    } catch (error: any) {
      message.error("Erro ao salvar unidade");
    }
  };

  // Masks
  const handleCnpjChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("cnpj", mascaraCnpj(e.target.value.replace(/\D/g, "")));
  };
  const handleCpfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("responsavelCpf", mascaraCpf(e.target.value.replace(/\D/g, "")));
  };
  const handleTelefoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("telefone", mascaraCelular(e.target.value.replace(/\D/g, "")));
  };

  // Format Fator de Ajuste Financeiro onBlur
  const handleFatorAjusteBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value && value !== "") {
      // Remove non-numeric except comma
      let numberValue = value.replace(/\./g, "").replace(",", ".");
      let floatValue = parseFloat(numberValue);
      if (!isNaN(floatValue)) {
        setValue("fatorAjusteFinanceiro", floatValue.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }), { shouldValidate: true });
      }
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-8">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-8">
          <span className="text-white text-2xl">{isEdit ? "Editar Unidade" : "Cadastro de Unidade"}</span>
        </div>
          <div className="flex items-center gap-2 mt-6">
            <Input
              className="h-4 w-4"
              type="checkbox"
              id="ativo"
              {...register("ativo", { required: true })}
              checked={watch("ativo")}
              onChange={e => setValue("ativo", e.target.checked)}
            />
            <label htmlFor="ativo">Ativo *</label>
          </div>
          {/*
            Separado de "Ativo" de propósito: a unidade de teste continua ativa
            para uso interno, mas não pode ser oferecida no orçamento público.
          */}
          <div className="flex items-center gap-2 mt-2">
            <Input
              className="h-4 w-4"
              type="checkbox"
              id="disponivelSite"
              {...register("disponivelSite")}
              checked={watch("disponivelSite")}
              onChange={e => setValue("disponivelSite", e.target.checked)}
            />
            <label htmlFor="disponivelSite">
              Disponível no site público
              <span className="block text-xs text-zinc-400">
                Aparece na página pública de orçamento
              </span>
            </label>
          </div>
        <div className="grid grid-cols-2 gap-6">
          <div className="col-span-2">
            <label>Nome *</label>
            <Input type="text" placeholder="Nome" {...register("nome", { required: "Nome é obrigatório" })} />
            {errors.nome && <span className="text-red-600">{errors.nome.message}</span>}
          </div>
          <div>
            <label>Fator de Ajuste Financeiro *</label>
            <Input
              type="text"
              placeholder="0,00"
              inputMode="decimal"
              {...register("fatorAjusteFinanceiro", {
                required: "Fator de ajuste é obrigatório",
                pattern: {
                  value: /^\d+(,\d{1,2})?$/,
                  message: "Formato inválido (ex: 1,23)",
                },
              })}
              onBlur={handleFatorAjusteBlur}
              defaultValue={""}
            />
            {errors.fatorAjusteFinanceiro && <span className="text-red-600">{errors.fatorAjusteFinanceiro.message}</span>}
          </div>
          <div>
            <label>CNPJ</label>
            <Input
              type="text"
              placeholder="00.000.000/0000-00"
              maxLength={18}
              {...register("cnpj")}
              onChange={handleCnpjChange}
            />
          </div>
          <div className="col-span-2">
            <label>Endereço Completo</label>
            <Input type="text" placeholder="Endereço Completo" {...register("enderecoCompleto")} />
          </div>
          <div>
            <label>Responsável</label>
            <Input type="text" placeholder="Responsável" {...register("responsavel")} />
          </div>
          <div>
            <label>CPF do Responsável</label>
            <Input
              type="text"
              placeholder="000.000.000-00"
              maxLength={14}
              {...register("responsavelCpf")}
              onChange={handleCpfChange}
              value={watch("responsavelCpf")}
            />
            {errors.responsavelCpf && <span className="text-red-600">{errors.responsavelCpf.message}</span>}
          </div>
          <div>
            <label>RG</label>
            <Input type="text" placeholder="RG" {...register("rg")} />
          </div>
          <div>
            <label>Órgão RG</label>
            <Input type="text" placeholder="Órgão RG" {...register("orgaoRg")} />
          </div>
          <div>
            <label>Telefone</label>
            <Input
              type="text"
              placeholder="(00) 00000-0000"
              maxLength={15}
              {...register("telefone")}
              onChange={handleTelefoneChange}
              value={watch("telefone")}
            />
          </div>
          <div>
            <label>Email</label>
            <Input
              type="email"
              placeholder="Email"
              {...register("email", {
                validate: (value) => {
                  if (!value) return true;
                  // Simple email regex
                  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || "Email inválido";
                },
              })}
            />
            {errors.email && <span className="text-red-600">{errors.email.message}</span>}
          </div>
        </div>
        <div className="flex justify-start p-2 mt-12 gap-8">
          <Button
            className="flex gap-2"
            variant="destructive"
            type="button"
            onClick={() => navigate("/unidades")}
          >
            Cancelar
          </Button>
          <Button className="flex gap-2" type="submit">
            Salvar
          </Button>
        </div>
      </form>
    </div>
  );
} 